import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { ACQUISITION_LABELS, api, mineralTitle, plural, type MapAnswer } from '../api.ts';
import { Icon } from '../components/Icon.tsx';
import { BASE_LAYERS, MapView, type BaseLayer } from '../components/MapView.tsx';

/**
 * Карта находок (ТЗ 9, макет 4).
 *
 * Фильтры те же, что в списке: карта — это второй способ смотреть на ту
 * же коллекцию, а не отдельное приложение.
 */
export function MapScreen() {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const [answer, setAnswer] = useState<MapAnswer | null>(null);
  const [countries, setCountries] = useState<{ country: string; count: number }[]>([]);
  const [showFilters, setShowFilters] = useState(false);
  // Подложка запоминается: кто смотрит находки по спутнику, смотрит так
  // всегда, и переключать её каждый заход — лишнее действие.
  const [layer, setLayer] = useState<BaseLayer>(
    () => (localStorage.getItem('druzza.map.layer') as BaseLayer | null) ?? 'scheme',
  );

  useEffect(() => {
    localStorage.setItem('druzza.map.layer', layer);
  }, [layer]);

  const query = useMemo(
    () => ({
      type: params.get('type') ?? undefined,
      country: params.get('country') ?? undefined,
      year: params.get('year') ?? undefined,
      q: params.get('q') ?? undefined,
    }),
    [params],
  );

  useEffect(() => {
    void api.mapPoints(query).then(setAnswer);
  }, [query]);

  useEffect(() => {
    void api.countries().then(setCountries);
  }, []);

  function setFilter(key: string, value: string | null) {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value);
    else next.delete(key);
    setParams(next);
  }

  const points = (answer?.points ?? []).map((p) => ({
    id: p.id,
    latitude: p.latitude,
    longitude: p.longitude,
    title: `${mineralTitle({ name: p.primary_mineral ?? '', name_ru: p.primary_mineral_ru }) || 'без названия'}`,
    subtitle: [p.inv_number, p.locality_name, p.trip_title].filter(Boolean).join(' · '),
    precision_m: p.precision_m,
    source: p.coord_source,
  }));

  const years = [...new Set((answer?.points ?? []).map((p) => p.year).filter(Boolean))].sort();

  return (
    <div className="app">
      <div className="topbar topbar--bordered">
        <button className="icon-btn" onClick={() => navigate(-1)} aria-label="Назад">
          <Icon name="back" className="icon muted" />
        </button>
        <div>
          <h2 className="topbar-title">Карта находок</h2>
          <div className="sub">
            {answer
              ? `${answer.points.length} ${plural(answer.points.length, 'образец', 'образца', 'образцов')}`
              : ' '}
          </div>
        </div>
        <div className="spacer" />
        <button
          className="icon-btn"
          onClick={() => setShowFilters((v) => !v)}
          aria-label="Фильтры"
          aria-expanded={showFilters}
        >
          <Icon name="filter" className="icon muted" />
        </button>
      </div>

      {showFilters && (
        <div className="search-area">
          <div className="label-key" style={{ marginTop: 0 }}>Поступление</div>
          <div className="segment">
            {(['found', 'purchased'] as const).map((type) => (
              <button
                key={type}
                className={query.type === type ? 'is-selected' : ''}
                onClick={() => setFilter('type', query.type === type ? null : type)}
              >
                {ACQUISITION_LABELS[type]}
              </button>
            ))}
            <button className={!query.type ? 'is-selected' : ''} onClick={() => setFilter('type', null)}>
              все
            </button>
          </div>

          {countries.length > 1 && (
            <>
              <div className="label-key">Страна</div>
              <div className="chips">
                {countries.map((c) => (
                  <button
                    key={c.country}
                    className={query.country === c.country ? 'chip' : 'chip chip--plain'}
                    onClick={() => setFilter('country', query.country === c.country ? null : c.country)}
                  >
                    {c.country}
                  </button>
                ))}
              </div>
            </>
          )}

          {years.length > 1 && (
            <>
              <div className="label-key">Год</div>
              <div className="chips">
                {years.map((year) => (
                  <button
                    key={year}
                    className={query.year === String(year) ? 'chip' : 'chip chip--plain'}
                    onClick={() => setFilter('year', query.year === String(year) ? null : String(year))}
                  >
                    {year}
                  </button>
                ))}
              </div>
            </>
          )}
        </div>
      )}

      {answer && answer.points.length === 0 ? (
        <div className="scroll empty">
          <Icon name="pin" className="icon icon-lg muted" />
          <h2>Точек пока нет</h2>
          <p className="muted small">
            Координаты появляются у выезда — один раз для всей привезённой партии,
            — или у отдельного образца. Снимки, сделанные дома, точек не дают.
          </p>
          <Link className="btn btn--primary" to="/trips/new">
            <Icon name="route" className="icon icon-sm" /> Завести выезд
          </Link>
        </div>
      ) : (
        <div className="map-wrap">
          <div className="map-layers">
            {BASE_LAYERS.map((item) => (
              <button
                key={item.key}
                type="button"
                className={item.key === layer ? 'chip' : 'chip chip--plain'}
                onClick={() => setLayer(item.key)}
              >
                {item.title}
              </button>
            ))}
          </div>
          <MapView
            points={points}
            layer={layer}
            onSelect={(id) => navigate(`/specimen/${id}`)}
          />
        </div>
      )}

      {answer && answer.hidden_by_home_zone > 0 && (
        <div className="map-note">
          <Icon name="alert" className="icon icon-sm" />
          <span>
            {answer.hidden_by_home_zone}{' '}
            {plural(answer.hidden_by_home_zone, 'точка', 'точки', 'точек')} скрыто домашней зоной
          </span>
        </div>
      )}
    </div>
  );
}
